import z from 'zod';
/** DR-3883: a review flag on the elected LLP status sheet — see `LLPStatusWarning`. */
const llpStatusWarningSchema = z.discriminatedUnion('code', [
    z.looseObject({
        code: z.literal('unsigned-winner'),
        documentId: z.string(),
        headerCsn: z.number().optional(),
        signedAlternative: z
            .looseObject({
            documentId: z.string(),
            headerCsn: z.number().optional(),
        })
            .optional(),
    }),
    z.looseObject({
        code: z.literal('clock-raised-by-single-sheet'),
        documentId: z.string(),
        headerCsn: z.number(),
        voteCsn: z.number(),
    }),
]);
export const llpStatusEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('llp-status-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable —
    // Brain always emits it (see the family's builder, pinned by its test). This schema is a
    // member of `webhookPayloadSchema`, so a REQUIRED field would make a consumer that
    // upgrades `@avisoma/shared` reject every older, unstamped envelope still in flight — and reject
    // ALL of them for as long as the consumer runs ahead of Brain's own deploy. Tighten to
    // required in a later release, once no unstamped envelope can still be in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    maxCSN: z.number(),
    llp: z.looseObject({
        documentId: z.string(),
        pageIds: z.array(z.string()),
        esn: z.string().optional(),
    }),
    nis: z
        .looseObject({
        documentId: z.string(),
        pageIds: z.array(z.string()),
        esn: z.string().optional(),
    })
        .optional(),
    parts: z.array(z.looseObject({
        partNumber: z.string(),
        serialNumber: z.string(),
        name: z.string(),
        assy: z.string().optional(),
        date: z.string().optional(),
        iin: z.string().optional(),
        tsn: z.number().optional(),
        csn: z.number(),
        lifeLimitCycles: z.number().optional(),
        remainingCycles: z.number().optional(),
        noLifeLimit: z.literal(true).optional(),
        engineVariants: z
            .array(z.looseObject({
            thrust: z.string(),
            csn: z.number().optional(),
            lifeLimitCycles: z.number().optional(),
            remainingCycles: z.number().optional(),
            noLifeLimit: z.literal(true).optional(),
        }))
            .optional(),
        partMovementPageIds: z.array(z.string()),
        // DR-4174 follow-up: additive and OPTIONAL. Absent when the row has attached cards.
        // A historical payload without the key parses exactly as before.
        traceMiss: z.enum(['no-card', 'empty-or-failed-card']).optional(),
        // DR-4034: additive and OPTIONAL — absence is "the surviving card agrees, or there is no
        // card". A historical payload without the key parses exactly as before.
        partNumberDisagreement: z
            .looseObject({
            cardPartNumbers: z.array(z.string()).min(1),
        })
            .optional(),
        events: z.array(z.looseObject({
            type: z.enum([
                'installation',
                'removal',
                'current',
                'end-of-lease',
                'end-of-operation',
                'shop-visit',
                'ferry-flight',
                'thrust-change',
                'delivery',
                'birth',
            ]),
            date: z.string().optional(),
            thrust: z.string().optional(),
            msn: z.string().optional(),
            tsn: z.number().optional(),
            csn: z.number(),
            // DR-3607: per-event engine clocks from the ENGINE TSN/CSN columns.
            // Additive and OPTIONAL — cards without those columns omit the keys.
            engineTsn: z.number().optional(),
            engineCsn: z.number().optional(),
            esn: z.string().optional(),
            reason: z.string().optional(),
            operator: z.string().optional(),
            // DR-3503: how `operator` was obtained. Additive and OPTIONAL — same catch policy as
            // `movement` so an out-of-vocabulary token cannot strand the part's history.
            operatorSource: z.enum(['model', 'document-propagated', 'carried']).optional().catch(undefined),
            aircraftReg: z.string().optional(),
            position: z.string().optional(),
            // DR-3269: the physical-movement axis, additive and OPTIONAL — absence is the field's
            // "document did not state it" reading, so a historical payload that carries no movement
            // at all parses exactly as before.
            //
            // Unlike `type` above, an out-of-vocabulary token here must NOT be rejected loudly: a
            // movement is an attribute of a real event, and the event itself is the delivery. Note
            // where the value actually goes: the producer validates and then ships its own unparsed
            // payload, so a bad token still egresses as printed — `.catch(undefined)` drops it on the
            // CONSUMER side, whenever a reader parses through this schema on shared >= 0.212.0.
            // Vocabulary at the source is the extraction sanitiser's job (`sanitiseRowMovement`).
            movement: z.enum(['installation', 'removal', 'none']).optional().catch(undefined),
            // DR-3935: the component's own P/N printed on THIS movement row. Additive and
            // OPTIONAL — absence is "the card has no per-row P/N column". Do not confuse
            // with the part identity `parts[].partNumber` (the LLP sheet number).
            partNumber: z.string().optional(),
            // DR-5766: Brain kept this row's engine empty on purpose (DR-5595): the card names a module
            // parent serial and the remarks do not confirm the engine. Additive and OPTIONAL — absent
            // on every other row, and on a row that states an `esn` (explicit engine evidence wins).
            // Same catch policy as `movement`: a bad token costs the field, never the event.
            esnWithheld: z.literal('parent-serial').optional().catch(undefined),
        })),
    })),
    // DR-3883: review flags on the elected sheet. Additive and OPTIONAL — absent when the winner is
    // signed and inside the clock's band. `.catch(undefined)`: the producer validates and then ships
    // its own unparsed payload, so a malformed entry degrades the whole list to `undefined` on the
    // CONSUMER side instead of failing the parse and stranding the engine's parts.
    warnings: z.array(llpStatusWarningSchema).optional().catch(undefined),
});
//# sourceMappingURL=llp-status-events-ready.webhook.js.map